// Podcasts UI islands (plan task 1.9): the Add-Feed modal toggle and
// the htmx glue the modal needs (error surfacing). All episode-list /
// download behavior is htmx-native (hx-post + podcast:changed trigger +
// server-driven polling) — this module only owns pure client-local
// presentation state, per the rendering-ownership rule.
import { Alpine } from "./alpine";
import { copyToClipboard } from "./clipboard";

Alpine.data("podcastsPage", () => ({
  showAddFeed(): void {
    const modal = document.getElementById("podcast-add-feed-modal");
    if (modal) modal.classList.remove("hidden");
  },
  hideAddFeed(): void {
    const modal = document.getElementById("podcast-add-feed-modal");
    if (modal) modal.classList.add("hidden");
    const err = document.getElementById("podcast-add-feed-error");
    if (err) err.classList.add("hidden");
  },
}));

// The detail page's actions are pure htmx; Alpine hosts the x-data the
// body declares (modal visibility) so CSP-bound Alpine stays happy.
Alpine.data("podcastDetail", () => ({
  unsubOpen: false,
  filterListenerInstalled: false,

  // Copy Feed URL (2026-11-03): the RSS URL never renders on the page —
  // the button carries it as data-feed-url (templ) and this reads it
  // off the CLICKED element's currentTarget (the icon is a child, so
  // $event.target would miss the dataset).
  async copyFeedUrl(e: Event): Promise<void> {
    const url = (e.currentTarget as HTMLElement).dataset.feedUrl;
    if (!url) return;
    await copyToClipboard(url, "Feed URL");
  },

  // Admin bulk file selection (§0 item 8 follow-up): ids of CHECKED
  // episode rows. Survives the list's htmx swaps (rows re-render fresh
  // and unchecked → reapplied on afterSwap); cleared after a successful
  // bulk delete. Plain array — deliberately NOT reactive state beyond
  // bulkCount, which the toolbar binds.
  bulkIds: [] as string[],
  bulkCount: 0,

  bulkSelectionLabel(): string {
    return this.bulkCount === 0 ? "" : this.bulkCount + " selected";
  },

  bulkSelectionClass(): string {
    return this.bulkCount === 0 ? "opacity-40 pointer-events-none" : "";
  },

  toggleBulkSelection(target: HTMLInputElement): void {
    const id = target.value;
    if (!id) return;
    if (target.checked) {
      if (!this.bulkIds.includes(id)) this.bulkIds.push(id);
    } else {
      this.bulkIds = this.bulkIds.filter((v) => v !== id);
    }
    this.bulkCount = this.bulkIds.length;
  },

  // After each swap the list re-renders unchecked; restore ticks for
  // ids still selected. Ids whose rows vanished (deleted / state
  // flipped) silently drop out.
  reapplyBulkSelection(): void {
    if (this.bulkIds.length === 0) return;
    document
      .querySelectorAll<HTMLInputElement>("input[name='episode-file-select']")
      .forEach((box) => {
        box.checked = this.bulkIds.includes(box.value);
      });
    // Drop ids whose checkboxes no longer exist.
    const present = new Set(
      Array.from(
        document.querySelectorAll<HTMLInputElement>("input[name='episode-file-select']"),
      ).map((b) => b.value),
    );
    this.bulkIds = this.bulkIds.filter((id) => present.has(id));
    this.bulkCount = this.bulkIds.length;
  },

  clearBulkSelection(): void {
    this.bulkIds = [];
    this.bulkCount = 0;
    document
      .querySelectorAll<HTMLInputElement>("input[name='episode-file-select']:checked")
      .forEach((box) => {
        box.checked = false;
      });
  },

  // Episode-list filters (§0 item 6, 2026-10-30): hide played/
  // downloaded rows and/or substring-filter titles+descriptions. Pure
  // presentation — the DOM IS the data (rows carry data-* attributes
  // rendered by templ); no query changes, no server round trip.
  // finishedThreshold 99.5% matches the server's finished rule.
  applyEpisodeFilter(): void {
    const input = (this as any).$refs.episodeFilterInput as HTMLInputElement | undefined;
    const box = (this as any).$refs.hideFinished as HTMLInputElement | undefined;
    const emptyNote = (this as any).$refs.filterEmptyNote as HTMLElement | undefined;
    const q = (input?.value ?? "").trim().toLowerCase();
    const hideFinished = box?.checked ?? false;

    let visible = 0;
    let total = 0;
    document.querySelectorAll<HTMLElement>("#episode-list [data-episode-row]").forEach((row) => {
      total++;
      const downloaded = row.dataset.downloaded === "true";
      const pct = parseFloat(row.dataset.listenPct ?? "0");
      const text = row.dataset.searchText ?? "";
      const hide =
        (hideFinished && (downloaded || pct >= 99.5)) ||
        (q !== "" && !text.toLowerCase().includes(q));
      row.classList.toggle("hidden", hide);
      if (!hide) visible++;
    });
    if (emptyNote) emptyNote.classList.toggle("hidden", visible > 0 || total === 0);
  },

  // Re-apply after every htmx swap of the list (polling + action
  // responses swap #episode-list wholesale): fresh rows must honor the
  // active filter AND restored bulk-selection ticks; a successful bulk
  // delete clears the selection. Installed once per page.
  initPodcastFilterPersistence(): void {
    if (this.filterListenerInstalled) return;
    this.filterListenerInstalled = true;
    document.addEventListener("htmx:afterSwap", () => {
      this.reapplyBulkSelection();
      this.applyEpisodeFilter();
    });
    // Successful bulk delete → the list swaps fresh; drop the state.
    document.addEventListener("htmx:afterRequest", (evt) => {
      const custom = evt as CustomEvent<{ successful: boolean; elt: HTMLElement }>;
      const elt = custom.detail.elt as HTMLElement | undefined;
      if (custom.detail.successful && elt && elt.id === "episode-bulk-delete") {
        this.clearBulkSelection();
      }
    });
  },
}));

// Add-feed form: surface the server's error text inline (the JSON error
// body arrives on non-2xx; htmx swaps nothing). Success redirects via
// HX-Redirect — nothing to do here.
export function initPodcastAddFeedForm(): void {
  const form = document.querySelector<HTMLFormElement>(
    '#podcast-add-feed-modal form[hx-post="/api/podcasts/feeds"], #podcast-add-feed-modal form',
  );
  if (!form) return;
  form.addEventListener("htmx:afterRequest", (evt) => {
    const custom = evt as CustomEvent<{
      successful: boolean;
      xhr: XMLHttpRequest;
    }>;
    if (custom.detail.successful) return;
    let message = "Could not add the feed — check the URL and try again.";
    try {
      const parsed = JSON.parse(custom.detail.xhr.responseText) as {
        error?: string;
      };
      if (parsed.error) message = parsed.error;
    } catch {
      // keep the default text
    }
    const err = document.getElementById("podcast-add-feed-error");
    if (err) {
      err.textContent = message;
      err.classList.remove("hidden");
    }
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initPodcastAddFeedForm);
} else {
  initPodcastAddFeedForm();
}
